// Dashboard grid engine (W12 Track A) — the P17 gadget-grid mechanism's
// client half. Stacked on the W1 drag kernel under one root
// (ui--work-drag ui--work-gadget-grid, P-102); `app/javascript/lib/work/
// grid_layout.js` is the pure seam this controller drives.
//
// Duties (spec §6 A2, in order of risk):
//   1. Hydration (P5)     — parse the SSR values, clamp against the DOM
//                           registry; sync state only, never rebuild.
//   2. Drop translation   — kernel ui--work:drop → translateDrop/applyOp →
//                           span rewrite + per-gesture layout-change.
//   3. Resize (D3)        — pointer grip (rAF-throttled) + Alt+Shift+Arrows;
//                           Esc/pointercancel restores pre-gesture spans.
//   4. Add/Remove         — tray Add button (keyboard path) + per-cell
//                           remove.
//   5. Edit session (D4)  — startEditing/stopEditing; exactly ONE
//                           dashboard-layout-commit per stopEditing; host
//                           ack reverts via the W2 seam (single-flight).
//   6. Reset/Clean        — edit-bar causes; change events, never commit.
//   7. Feedback-loop guard (RB5/P-207) — this engine listens ONLY to the
//                           kernel drop and the HOST ack. It never listens
//                           to its own dashboard-layout-* / gadget-refresh
//                           emissions; only the ack writes state back.
//   8. Teardown           — listeners off, rAF cancelled, scroll lock
//                           restored; Turbo cache restore re-hydrates.
//   9. Announcements      — the live region gets the work.dashboard.announce.*
//                           copy per gesture (P-111).
//
// Boundary (P-208): events are the only API — NO fetch/XHR/Request/
// window.location in this file (grep-pinned). Gadget content is host
// composition; persistence is host-owned (the commit PATCH happens in the
// host's glue island).

import { Controller } from "@hotwired/stimulus"
import {
  GRID_CONTAINER_ID,
  TRAY_CONTAINER_ID,
  MAX_SPAN,
  translateDrop,
  applyOp,
  clampEntry,
} from "../../../lib/work/grid_layout.js"
import { adoptOrRollback } from "../../../lib/work/mutation_seam.js"
import { directionOf } from "../../../lib/work/rank.js"

const KERNEL_DROP_EVENT = "ui--work:drop"
const LAYOUT_CHANGE_EVENT = "ui--work:dashboard-layout-change"
// ACK-MANDATORY CONTRACT: every LAYOUT_COMMIT_EVENT this engine emits MUST be
// answered by the host glue with a LAYOUT_ACK_EVENT ({ok, layout?}) on the
// root. While the ack is outstanding the engine is single-flight — no new
// commits, no re-entry (startEditing/stopEditing no-op) — so a host that
// drops the commit wedges the session. The ACK_TIMEOUT_MS safety below
// reverts to the pre-edit snapshot when the ack never arrives, but the host
// must not rely on it: answer every commit.
const LAYOUT_COMMIT_EVENT = "ui--work:dashboard-layout-commit"
const GADGET_REFRESH_EVENT = "ui--work:gadget-refresh"
const GADGET_RESIZE_EVENT = "ui--work:gadget-resize"
const LAYOUT_ACK_EVENT = "ui--work:dashboard-layout-ack"

// The dropped-ack safety net (one-shot; cancelled by ANY ack so a
// slow-but-legit ack never races the revert): on fire, the working layout
// reverts to the pre-edit snapshot and the single-flight clears.
const ACK_TIMEOUT_MS = 10_000

// Row unit of the D1 grid (`auto-rows-[2.5rem]`); the live measurement
// below overrides this per instance, this is only the SSR fallback.
const ROW_UNIT_FALLBACK = 40

// Safety fallbacks for the announce copy (the SSR announcements value is
// the real source — kit locale strings, always emitted).
const DEFAULT_ANNOUNCEMENTS = {
  move: "Gadget moved",
  resize: "Gadget resized",
  add: "Gadget added",
  remove: "Gadget removed",
  reset: "Layout reset to defaults",
  clean: "Layout cleaned",
}

let tokenCounter = 0

// Opaque per-gesture token, the kernel's shape (counter + uuid when the
// platform has one) — P-109's add and the keyboard resize path carry one.
function newGestureToken() {
  tokenCounter += 1
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === "function") {
    return `work-grid-${tokenCounter}-${globalThis.crypto.randomUUID()}`
  }
  return `work-grid-${tokenCounter}-${Date.now()}`
}

export default class DashboardGridController extends Controller {
  static targets = ["grid", "tray", "announcer", "toastRegion"]

  static values = {
    layout: { type: Array, default: [] },
    defaults: { type: Array, default: [] },
    editing: { type: Boolean, default: false },
    announcements: { type: Object, default: {} },
    toastTitle: { type: String, default: "" },
    toastDescription: { type: String, default: "" },
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  connect() {
    // 0. TEARDOWN-THEN-REBIND (D1, the timeline/backlog precedent): a double
    // connect (Turbo cache restore racing a morph) must not orphan the first
    // bind's closures — they stay attached to the root and disconnect()
    // would remove only the newest refs, leaving ghost handlers on every
    // event. Any in-flight gesture is cancelled first, so no ghost gesture
    // survives the rebind either.
    this._teardown()

    // 1. HYDRATION (P5): the SSR render already placed one wrapper per
    // rendered gadget with span classes; hydration only validates state.
    this._registry = this._buildRegistry()
    this.layoutValue = this._hydrateLayout(this.layoutValue)
    this._syncDomToLayout()
    this._syncEditingChrome()

    // An editing SSR IS an edit session (RB2): the pre-edit snapshot is the
    // hydrated SSR layout, so a commit-ack revert on an editing SSR (F4)
    // always has a layout to restore. startEditing() refreshes it.
    if (this.editingValue) this._editSnapshot = this.currentLayout()

    // Bound listeners (kept for teardown — P-207's guard is structural: the
    // only events this engine LISTENS to are the kernel drop and the host
    // ack; never its own emissions).
    this._onKernelDropBound = (event) => this._onKernelDrop(event)
    this._onAckBound = (event) => this._onLayoutAck(event)
    this._onPointerDownBound = (event) => this._onResizePointerDown(event)
    this._onKeyDownBound = (event) => this._onKeyDown(event)
    this._onTrayClickBound = (event) => this.addGadget(event)

    this.element.addEventListener(KERNEL_DROP_EVENT, this._onKernelDropBound)
    this.element.addEventListener(LAYOUT_ACK_EVENT, this._onAckBound)
    this.element.addEventListener("pointerdown", this._onPointerDownBound)
    this.element.addEventListener("keydown", this._onKeyDownBound)
    this.element.addEventListener("click", this._onTrayClickBound)

    this._resize = null
    this._awaitingAck = false
    this._scrollLocks = []
    this._gestureEscBound = false
    this._gestureEscHandler = null
  }

  disconnect() {
    // 8. TEARDOWN: listeners off, rAF cancelled, scroll lock restored, resize
    // gesture dropped mid-flight (styling restored). A Turbo cache restore
    // re-runs connect() and re-hydrates from the SSR values (P5) — no stale
    // working layout survives.
    this._teardown()
    this._resize = null
    this._awaitingAck = false
  }

  // The D1 teardown — the SINGLE unbind path for connect() (rebind-safe) and
  // disconnect(): the five root listeners come off with their CURRENT bound
  // refs (nulled after), the gesture Esc delivery unbinds, an in-flight
  // resize cancels (styling + scroll lock restored) and the ack safety timer
  // clears. All state guards tolerate a first-ever call (fields unset).
  _teardown() {
    if (this._onKernelDropBound) {
      this.element.removeEventListener(KERNEL_DROP_EVENT, this._onKernelDropBound)
      this.element.removeEventListener(LAYOUT_ACK_EVENT, this._onAckBound)
      this.element.removeEventListener("pointerdown", this._onPointerDownBound)
      this.element.removeEventListener("keydown", this._onKeyDownBound)
      this.element.removeEventListener("click", this._onTrayClickBound)
      this._onKernelDropBound = null
      this._onAckBound = null
      this._onPointerDownBound = null
      this._onKeyDownBound = null
      this._onTrayClickBound = null
    }
    this._unbindGestureEsc()
    this._clearAckTimer()
    this._cancelResizeFrame()
    this._finishResize({ cancelled: true })
    this._restoreScrollLocks()
  }

  // ── Hydration + registry (A2.1, B1) ───────────────────────────────────────

  // The DOM is the registry contract (B1): every wrapper carrying
  // data-gadget-id (grid AND tray) contributes its spans and min spans.
  // The engine clamps from these — never from JS-side constants.
  _buildRegistry() {
    const registry = {}
    this.element.querySelectorAll("[data-gadget-id]").forEach((wrapper) => {
      const id = wrapper.getAttribute("data-gadget-id")
      if (!id || registry[id]) return
      registry[id] = {
        width: this._intAttr(wrapper, "data-width", 6),
        height: this._intAttr(wrapper, "data-height", 4),
        min_width: this._intAttr(wrapper, "data-min-width", 2),
        min_height: this._intAttr(wrapper, "data-min-height", 2),
      }
    })
    return registry
  }

  _intAttr(element, name, fallback) {
    const value = Number(element.getAttribute(name))
    return Number.isFinite(value) && value >= 1 ? Math.floor(value) : fallback
  }

  // Validate + clamp every SSR entry against the registry (P-201): unknown
  // ids stay in the working layout (CONEK-4938 — payload-preserved,
  // render-skipped); registered ids clamp to [min_width, 12] / [min_height, 12]
  // (the D1 ceiling — the SSR clamp and the CSS safelist end at 12).
  _hydrateLayout(layout) {
    const source = Array.isArray(layout) ? layout : []
    return source.map((entry) => clampEntry(entry, this._registry) || entry)
  }

  // The wrappers are already in the DOM from SSR — this reconciles the DOM
  // with the working layout (hydration fix-up, Turbo restore, drop
  // rollbacks, ack revert/adopt). Never rebuilds (P5): spans are rewritten,
  // grid↔tray membership re-seated, grid order restored — the ack paths
  // need the DOM to actually match the restored layout, or a reverted move
  // / an ack layout that omits a gadget leaves a ghost in the grid.
  _syncDomToLayout() {
    const grid = this.hasGridTarget ? this.gridTarget : null
    const seen = new Set()
    const ordered = []

    this.layoutValue.forEach((entry) => {
      const wrapper = this._wrapperFor(entry.gadget_id)
      if (!wrapper) return // render-skipped (unknown id) — payload-preserved
      seen.add(entry.gadget_id)
      // Membership re-seat (grid side): a tray-resident wrapper whose id is
      // in the layout belongs in the grid — a refused kernel remove (or a
      // refused add's rollback) can strand it in the tray. Re-parent FIRST,
      // then _leaveTray: its emptied-row sweep must see the wrapper already
      // gone from its row (the old order ran the guard while the wrapper
      // was still INSIDE the row — dead code; every tray→grid reconciliation
      // leaked a ghost row + inert Add button).
      if (grid && wrapper.parentElement !== grid) {
        grid.appendChild(wrapper)
        this._leaveTray(wrapper)
      }
      this._applySpans(wrapper, entry)
      if (grid) ordered.push(wrapper)
    })

    if (grid) {
      // Grid wrappers absent from the layout (a failed add, an ack layout
      // that omits the gadget) lose their spans and return to the tray —
      // never a ghost in the grid.
      Array.from(grid.querySelectorAll("[data-gadget-id]")).forEach((wrapper) => {
        if (seen.has(wrapper.getAttribute("data-gadget-id"))) return
        this._returnToTray(wrapper)
      })

      // Order reconciliation: the row-flow grid's visual order IS the DOM
      // order — re-append the wrappers in layout order, but only when the
      // DOM order actually differs (P5: sync, never rewrite for its own
      // sake).
      const current = Array.from(grid.children).filter(
        (el) => el.nodeType === 1 && el.hasAttribute("data-gadget-id")
      )
      if (current.length !== ordered.length || current.some((el, index) => el !== ordered[index])) {
        ordered.forEach((wrapper) => grid.appendChild(wrapper))
      }
    }

    // Membership changed → the tray's empty note/list swap follows.
    this._syncTrayEmpty()
  }

  _wrapperFor(gadget_id) {
    return this.element.querySelector(`[data-gadget-id="${CSS.escape(String(gadget_id))}"]`)
  }

  // Rewrite the span utilities on a wrapper (the D1 classes). Tray items
  // carry no spans — passing a null entry strips them.
  _applySpans(wrapper, entry) {
    // Spread BEFORE removing — DOMTokenList is live; removing while
    // forEach-iterating it skips entries (the strip-then-add would leave a
    // stale span class behind).
    [...wrapper.classList].forEach((cls) => {
      if (/^col-span-\d+$/.test(cls) || /^row-span-\d+$/.test(cls)) wrapper.classList.remove(cls)
    })
    if (entry) {
      wrapper.classList.add(`col-span-${entry.width}`, `row-span-${entry.height}`)
      wrapper.setAttribute("data-width", String(entry.width))
      wrapper.setAttribute("data-height", String(entry.height))
    } else {
      // A stripped wrapper is a tray tile — no grid spans, no span data.
      wrapper.removeAttribute("data-width")
      wrapper.removeAttribute("data-height")
    }
  }

  // ── Emission + announcements (A2.2/A2.9) ──────────────────────────────────

  // All engine events bubble from the ROOT (events are the only API). The
  // layout payload is ALWAYS the full working layout — preserved unknown
  // entries included at their original index (CONEK-4938/P-204).
  _emitLayoutChange(cause, { gadget_id = null, gesture_token = null, source = "pointer" } = {}) {
    this._emit(LAYOUT_CHANGE_EVENT, {
      layout: this.currentLayout(),
      cause,
      gadget_id,
      gesture_token,
      source,
    })
    this._announce(cause, gadget_id)
  }

  _emit(name, detail) {
    this.element.dispatchEvent(
      new CustomEvent(name, { detail, bubbles: true, cancelable: false })
    )
  }

  // The kit live region (P-111): every gesture updates it with the matching
  // announce copy. The SSR announcements value (work.dashboard.announce.*)
  // is the source; DEFAULT_ANNOUNCEMENTS is only a fallback.
  _announce(cause, gadget_id) {
    if (!this.hasAnnouncerTarget) return
    const strings = this.announcementsValue || {}
    const template = strings[cause] || DEFAULT_ANNOUNCEMENTS[cause]
    if (!template) return
    this.announcerTarget.textContent = gadget_id ? `${template}: ${gadget_id}` : template
  }

  // ── Public API (W12-D4/D7) ────────────────────────────────────────────────

  // The persisted-state accessor — the FULL working layout (host saves it
  // verbatim from the commit payload).
  currentLayout() {
    return (this.layoutValue || []).map((entry) => ({ ...entry }))
  }

  // Host-side state push (e.g. an ack carrying a canonical layout). Clamps,
  // syncs the DOM, writes nothing else. Never called by the engine itself
  // (P-207).
  setLayout(layout) {
    this.layoutValue = this._hydrateLayout(Array.isArray(layout) ? layout : [])
    this._syncDomToLayout()
  }

  // Restore the pre-edit snapshot (W12-D4). The kernel's revert(token) owns
  // DOM-position rollbacks for in-flight kernel gestures; this restores the
  // engine's working layout — the commit-ack revert path (F4).
  revertLayout() {
    if (!Array.isArray(this._editSnapshot)) return false
    this.setLayout(this._editSnapshot)
    return true
  }

  // Edit session (D4). NOTE the SSR rule (findings ledger): view SSR renders
  // NO edit chrome — a view page enters edit with a server re-render
  // (Hotwire). startEditing()/stopEditing() toggle the RUNTIME state over an
  // editing:true SSR (Done hides the chrome; re-opening shows it again).
  // While a commit ack is outstanding the snapshot is committed to the
  // pending ack — re-snapshotting here would let a later !ok "revert" to
  // the layout the host just refused, so startEditing waits it out.
  startEditing() {
    if (this.editingValue || this._awaitingAck) return
    this._editSnapshot = this.currentLayout()
    this.editingValue = true
  }

  // EXACTLY ONE commit per call (P-204) — and while an ack is outstanding
  // the guard makes a second call a no-op (the stale-ack single-flight,
  // P-205). Neither Reset nor Clean ever reaches here (RB7). The commit
  // arms the ACK_TIMEOUT_MS safety: a host that never answers reverts via
  // _onAckTimeout instead of wedging the session (the ACK-MANDATORY
  // CONTRACT on LAYOUT_COMMIT_EVENT).
  stopEditing() {
    if (!this.editingValue || this._awaitingAck) return
    this._awaitingAck = true
    this.editingValue = false
    this._clearAckTimer()
    this._ackTimer = setTimeout(() => this._onAckTimeout(), ACK_TIMEOUT_MS)
    this._emit(LAYOUT_COMMIT_EVENT, { layout: this.currentLayout() })
  }

  // Runtime mirror of the RB2 SSR gating: the edit chrome (tray, edit bar,
  // grips, remove buttons) and the kernel item attributes follow the
  // editing state in both directions — Stimulus target wiring means the
  // attr flip alone wires/unwires the kernel's p-dnd sources.
  editingValueChanged() {
    // A stopEditing (or any host-driven editing flip) while a pointer
    // resize is LIVE ends the gesture CANCELLED (round-2): pre-gesture
    // spans restored, no change event riding into the _awaitingAck window,
    // and the chrome sync below never hides a captured grip mid-gesture.
    // Safe at SSR init — _resize is unset until connect() and the guard
    // skips.
    if (!this.editingValue && this._resize) this._finishResize({ cancelled: true })
    if (this.hasGridTarget) this._syncEditingChrome()
  }

  _syncEditingChrome() {
    const editing = this.editingValue
    this.element.querySelectorAll("[data-gadget-id]").forEach((wrapper) => {
      if (editing) {
        wrapper.setAttribute("data-ui--work-drag-target", "item")
        wrapper.setAttribute("data-item-id", wrapper.getAttribute("data-gadget-id"))
      } else {
        wrapper.removeAttribute("data-ui--work-drag-target")
        wrapper.removeAttribute("data-item-id")
      }
    })

    this.element.querySelectorAll("[data-work-dashboard-edit-bar]").forEach((el) => {
      el.hidden = !editing
    })
    if (this.hasTrayTarget) this.trayTarget.hidden = !editing

    this.element
      .querySelectorAll(
        '[data-ui--work-drag-target="handle"], [data-work-gadget-resize], [data-work-gadget-remove-id]'
      )
      .forEach((el) => {
        // Tray-resident tiles keep their grid-only chrome hidden (resize
        // grip + remove button are grid controls); the drag grip stays live
        // in the tray — dragging a tray tile into the grid is the pointer
        // add path.
        const gridControl =
          el.hasAttribute("data-work-gadget-resize") || el.hasAttribute("data-work-gadget-remove-id")
        el.hidden = gridControl && el.closest("[data-work-tray-items]") ? true : !editing
      })
  }

  // ── Drop translation (A2.2, P-202) ────────────────────────────────────────

  // The kernel has ALREADY re-parented the item by the time ui--work:drop
  // fires; this translates the neighbour delta into a layout op, applies it
  // immutably, and reconciles the DOM (spans in, spans out). Applied ops
  // emit the per-gesture change; refused ops (unknown id, duplicate add,
  // tray→tray noise) roll the DOM back to the working layout — no state
  // change, no event, no kernel revert token needed.
  _onKernelDrop(event) {
    if (!this.editingValue) return
    const drop = event.detail || {}
    const op = translateDrop(drop, this.layoutValue)
    if (!op) return

    const result = applyOp(this.layoutValue, op, this._registry, this.defaultsValue)
    if (!result.applied) {
      this._syncDomToLayout()
      return
    }

    this.layoutValue = result.layout
    const gadget_id = op.gadget_id

    if (op.op === "add") {
      // The kernel re-parented the item into the grid BEFORE this event
      // fires. _leaveTray sweeps the vacated tray row (cell + Add button —
      // no inert control lingers) and restores the grid chrome (editing →
      // unhide): an SSR tray tile carries the nodes HIDDEN and a
      // round-tripped tile had them hidden on the way in (_returnToTray) —
      // the pointer path missed that unhide until the round-2 fix (the
      // grip stayed dead until editing toggled).
      const wrapper = this._wrapperFor(gadget_id)
      if (wrapper) {
        this._leaveTray(wrapper)
        this._applySpans(wrapper, this._entryFor(gadget_id))
      }
    }

    if (op.op === "remove") {
      // The kernel's _applySlot parked the wrapper loose in the aside or
      // inside another row's cell — outside [data-work-tray-items], where
      // the empty-note count misses it. Re-seat it properly (spans stripped,
      // grid chrome hidden, fresh row + live Add button).
      const wrapper = this._wrapperFor(gadget_id)
      if (wrapper) this._returnToTray(wrapper)
    }

    // Tray membership changed on add AND remove — the empty note and the
    // list swap must follow (P-106), or a drained tray keeps its row and
    // note stale.
    this._syncTrayEmpty()

    this._emitLayoutChange(op.op, {
      gadget_id,
      gesture_token: drop.gesture_token || null,
      source: drop.source || "pointer",
    })
  }

  _entryFor(gadget_id) {
    return this.layoutValue.find((entry) => entry.gadget_id === gadget_id) || null
  }

  // ── Add / Remove (A2.4, P-109) ────────────────────────────────────────────

  // The tray Add button — the KEYBOARD path (P-109/RB4): appends the gadget
  // at layout end with its registry default spans, moves the full frame out
  // of the tray into the grid (the tray item IS a GadgetFrame), and drops
  // the id from the tray list. The tray is an unordered list — no slot math.
  //
  // The click is DELEGATED at the root (see _onTrayClickBound): runtime-
  // regenerated Add buttons (the grid→tray re-seat) cannot ride Stimulus's
  // connect-time data-action wiring, so every tray Add button — SSR or
  // regenerated — goes through one delegated path.
  addGadget(event) {
    if (!this.editingValue) return
    const target = event.target instanceof Element ? event.target : null
    const button = target ? target.closest("[data-work-gadget-add-id]") : null
    const gadget_id = button ? button.getAttribute("data-work-gadget-add-id") : null
    if (!gadget_id) return

    const op = { op: "add", gadget_id, before_id: null, after_id: null }
    const result = applyOp(this.layoutValue, op, this._registry, this.defaultsValue)
    if (!result.applied) return

    // Commit the layout BEFORE the DOM work — the span rewrite reads the
    // NEW entry (_entryFor over the working layout; the old order answered
    // null and stripped the added wrapper's spans, the drop add path's
    // order).
    this.layoutValue = result.layout

    const trayItem = this.trayTarget.querySelector(`[data-gadget-id="${CSS.escape(gadget_id)}"]`)
    if (trayItem) {
      // Re-parent FIRST, then let _leaveTray sweep the vacated row and
      // restore the grid chrome (editing → unhide) — the SAME tray→grid seam
      // the drop-add and the ack reconciliation use (round-2: both add paths
      // gain their chrome through one seam, so neither can miss the unhide
      // again). The old code captured the row before the move because
      // closest() reads the NEW ancestor chain; the sweep no longer needs
      // the capture.
      this.gridTarget.appendChild(trayItem)
      this._leaveTray(trayItem)
      this._applySpans(trayItem, this._entryFor(gadget_id))
    }
    this._syncTrayEmpty()
    this._emitLayoutChange("add", { gadget_id, gesture_token: newGestureToken(), source: "keyboard" })
  }

  // The per-cell remove button (edit-only): the gadget leaves the working
  // layout and returns to the tray (grid→tray drop semantics without a
  // gesture).
  removeGadget(event) {
    if (!this.editingValue) return
    const button = event.currentTarget
    const gadget_id = button.getAttribute("data-work-gadget-remove-id")
    if (!gadget_id) return

    const op = { op: "remove", gadget_id }
    const result = applyOp(this.layoutValue, op, this._registry, this.defaultsValue)
    if (!result.applied) return

    const wrapper = this._wrapperFor(gadget_id)
    if (wrapper) this._returnToTray(wrapper)
    this.layoutValue = result.layout
    this._syncTrayEmpty()
    this._emitLayoutChange("remove", { gadget_id, gesture_token: newGestureToken(), source: "pointer" })
  }

  // Tray empty state (P-106): the SSR tray renders the list hidden when it
  // STARTS empty (aligned with the runtime drain state — the engine swaps
  // the same hidden attribute at runtime, so SSR and runtime agree).
  _syncTrayEmpty() {
    if (!this.hasTrayTarget) return
    const list = this.trayTarget.querySelector("[data-work-tray-items]")
    const emptyNote = this.trayTarget.querySelector("[data-work-tray-empty]")
    if (!list || !emptyNote) return
    const drained = list.querySelectorAll("[data-gadget-id]").length === 0
    emptyNote.hidden = !drained
    list.hidden = drained
  }

  // Grid → tray re-seat (the remove path AND the layout reconciliation):
  // spans stripped, grid-only chrome (resize grip + remove button) hidden —
  // a tray tile is inert — and the wrapper re-parented into
  // [data-work-tray-items] in a FRESH row with a working Add button. The
  // kernel's _applySlot parks removed wrappers loose in the aside or inside
  // another row's cell; without the re-seat the empty-note count misses the
  // gadget and P-109's keyboard re-add dies after the first remove.
  _returnToTray(wrapper) {
    this._applySpans(wrapper, null)
    this._setTrayChrome(wrapper, true)
    if (!this.hasTrayTarget) return
    const list = this.trayTarget.querySelector("[data-work-tray-items]")
    if (!list) return
    const gadget_id = wrapper.getAttribute("data-gadget-id")

    const row = document.createElement("div")
    row.className = "flex items-center gap-2"
    const cell = document.createElement("div")
    cell.className = "min-w-0 flex-1"
    cell.appendChild(wrapper)
    row.appendChild(cell)

    const button = document.createElement("button")
    button.type = "button"
    button.className =
      "border-border text-foreground-medium inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    button.setAttribute("aria-label", this._gadgetTitle(wrapper) || gadget_id || "")
    button.setAttribute("data-work-gadget-add-id", gadget_id)
    // Donate the kit's plus icon from a sibling tray button when one exists
    // (no JS-side icon registry — P-108). A DRAINED tray has no sibling, so
    // the aside's SSR-rendered hidden donor (data-work-add-icon-source) is
    // the fallback — the regenerated pill matches the SSR appearance in
    // every tray state (round-2 MINOR: it used to render iconless AND
    // textless).
    const icon =
      list.querySelector("[data-work-gadget-add-id] svg") ||
      this.trayTarget.querySelector("[data-work-add-icon-source] svg")
    if (icon) button.appendChild(icon.cloneNode(true))
    row.appendChild(button)

    list.appendChild(row)
    this._syncTrayEmpty()
  }

  // The inverse re-seat (tray → grid): the wrapper's grid chrome returns to
  // the editing state's visibility (a view-mode setLayout must not surface
  // edit chrome), and the tray's EMPTIED rows are swept. The caller
  // re-parents the wrapper into the grid BEFORE calling this — the sweep
  // must see the wrapper already gone from its row. The emptiness guard
  // stays live and correct for every caller: rows are one-per-gadget by
  // construction, but the kernel's _applySlot can park a wrapper INSIDE
  // another row's cell, and that row (still holding its own tile) must
  // survive.
  _leaveTray(wrapper) {
    this._setTrayChrome(wrapper, !this.editingValue)
    if (!this.hasTrayTarget) return
    const list = this.trayTarget.querySelector("[data-work-tray-items]")
    if (!list) return
    list.querySelectorAll(":scope > div").forEach((row) => {
      if (!row.querySelector("[data-gadget-id]")) row.remove()
    })
  }

  _setTrayChrome(wrapper, hidden) {
    wrapper
      .querySelectorAll("[data-work-gadget-resize], [data-work-gadget-remove-id]")
      .forEach((el) => {
        el.hidden = hidden
      })
  }

  // Best-effort accessible name for a regenerated Add button: the frame's
  // rendered title (WidgetFrame renders it in an h3), falling back to the
  // gadget id. The SSR Add button names itself from frame_options — runtime
  // regeneration has no defs, only the DOM.
  _gadgetTitle(wrapper) {
    const heading = wrapper.querySelector("h3")
    return heading ? heading.textContent.trim() : ""
  }

  // ── Refresh (A2 / D5, P-206) ──────────────────────────────────────────────

  // Event-delegated from the frame's refresh control (Track B ships no
  // controller). ALWAYS emits gadget-refresh; auto mode ALSO reloads the
  // gadget's Turbo Frame natively (the merged keyboard-nav "src" in frame
  // idiom). event mode: the event alone is the action — the host listens.
  refreshGadget(event) {
    const gadget_id = event.currentTarget.getAttribute("data-work-gadget-refresh")
    if (!gadget_id) return

    this._emit(GADGET_REFRESH_EVENT, { gadget_id, source: "pointer" })

    if ((this._frameRefreshMode(gadget_id) || "auto") !== "auto") return
    const frame = this.element.querySelector(
      `turbo-frame[data-work-gadget-frame="${CSS.escape(gadget_id)}"]`
    )
    if (frame && typeof frame.reload === "function") frame.reload()
  }

  _frameRefreshMode(gadget_id) {
    const wrapper = this._wrapperFor(gadget_id)
    return wrapper ? wrapper.getAttribute("data-refresh-mode") : null
  }

  // ── Reset / Clean (A2.6, P-110/RB7) ───────────────────────────────────────

  // Reset restores the HOST-PASSED defaults; unregistered entries from the
  // current layout ride (CONEK-4938). Clean empties the registered layout.
  // Both emit dashboard-layout-change — NEVER a commit (persistence stays
  // Done-gated; the host decides whether a reset/clean persists).
  resetLayout() {
    if (!this.editingValue) return
    const result = applyOp(this.layoutValue, { op: "reset" }, this._registry, this.defaultsValue)
    if (!result.applied) return
    this.layoutValue = result.layout
    this._syncDomToLayout()
    this._emitLayoutChange("reset", { source: "pointer" })
  }

  cleanLayout() {
    if (!this.editingValue) return
    const result = applyOp(this.layoutValue, { op: "clean" }, this._registry, this.defaultsValue)
    if (!result.applied) return
    this.layoutValue = result.layout
    this._syncDomToLayout()
    this._emitLayoutChange("clean", { source: "pointer" })
  }

  // ── Commit-ack (A2.5, P-205) ──────────────────────────────────────────────

  // The host's consumption event, dispatched on the root: {ok, layout?}.
  // Single-flight: while an ack is outstanding this is the ONLY write path
  // (the feedback-loop guard's mirror — the engine never acts on its own
  // emissions). !ok reverts to the pre-edit snapshot through the W2 seam's
  // F4 shape and surfaces EXACTLY ONE kit toast. A MALFORMED ack (ok not a
  // boolean) is normalized to !ok: left alone the seam would answer
  // "ignored" — neither callback runs, _awaitingAck never clears, and one
  // bad host event wedges the edit session permanently.
  _onLayoutAck(event) {
    if (!this._awaitingAck) return // stale ack — ignored (P-205)
    this._clearAckTimer() // cancel-on-ack is mandatory — a slow-but-legit ack must not race the safety revert
    const ack = event.detail || {}
    const normalized = typeof ack.ok === "boolean" ? ack : { ...ack, ok: false }

    adoptOrRollback({
      ack: normalized,
      region: this.hasToastRegionTarget ? this.toastRegionTarget : null,
      applyAdopt: () => {
        if (ack.layout) this.setLayout(ack.layout)
        this._editSnapshot = null
        this._awaitingAck = false
      },
      applyRevert: () => {
        this.revertLayout()
        this._editSnapshot = null
        this._awaitingAck = false
      },
      errorMessage: [this.toastTitleValue, this.toastDescriptionValue].filter(Boolean).join(" — "),
    })
  }

  _clearAckTimer() {
    if (!this._ackTimer) return
    clearTimeout(this._ackTimer)
    this._ackTimer = null
  }

  // The one-shot dropped-ack safety (ACK_TIMEOUT_MS): the host never
  // answered the commit. Revert to the pre-edit snapshot and clear the
  // single-flight so the session can commit again — a late ack after this
  // is stale (P-205) and ignored. Quiet by design: the ack !ok path owns
  // the revert toast; this net only exists so the session cannot wedge.
  _onAckTimeout() {
    this._ackTimer = null
    if (!this._awaitingAck) return
    this.revertLayout()
    this._editSnapshot = null
    this._awaitingAck = false
  }

  // ── Resize (A2.3, D3, P-203) ──────────────────────────────────────────────

  // Pointer gesture on a resize grip: rAF-throttled track math (row units
  // vertical, column units horizontal), live span preview, Esc/pointercancel
  // restore. The grip carries the gadget id; the engine owns every listener.
  _onResizePointerDown(event) {
    if (!this.editingValue) return
    // Gesture-lifecycle guards: a second grip's pointerdown must never
    // overwrite the live gesture (it would leak the first grip's listeners),
    // and only the primary button starts a resize (a right-drag must not).
    if (this._resize) return
    if (event.button !== 0) return
    const grip = event.target.closest("[data-work-gadget-resize]")
    if (!grip) return

    const wrapper = grip.closest("[data-gadget-id]")
    const gadget_id = wrapper && wrapper.getAttribute("data-gadget-id")
    const entry = gadget_id ? this._entryFor(gadget_id) : null
    if (!wrapper || !entry) return

    event.preventDefault()
    const metrics = this._gridMetrics()
    const start = { x: event.clientX, y: event.clientY, width: entry.width, height: entry.height }

    this._resize = {
      gadget_id,
      wrapper,
      grip,
      start,
      pointerId: event.pointerId,
      last: null,
      frame: null,
      keyboard: false,
    }

    try {
      grip.setPointerCapture(event.pointerId)
    } catch {
      // jsdom/pointer-less environments — the gesture still tracks via the
      // root listeners; capture is a browsers-only nicety.
    }

    this._setPointerMoveListeners(grip)
    this._bindGestureEsc()
    this._pushScrollLock()
    this._announce("resize", gadget_id)
  }

  // Document-level Esc delivery while a pointer gesture is live (the W1
  // kernel's _bindEsc precedent): preventDefault() on pointerdown suppresses
  // the compatibility mousedown and therefore focus transfer to the grip, so
  // a mouse user's Escape targets document.body — a root-scoped listener
  // would never see it and the D3 cancel would be dead in the primary mouse
  // flow. Torn down with the gesture (_finishResize) and in disconnect.
  _bindGestureEsc() {
    if (this._gestureEscBound) return
    this._gestureEscBound = true
    this._gestureEscHandler = (event) => this._onKeyDown(event)
    document.addEventListener("keydown", this._gestureEscHandler)
  }

  _unbindGestureEsc() {
    if (!this._gestureEscBound) return
    this._gestureEscBound = false
    document.removeEventListener("keydown", this._gestureEscHandler)
    this._gestureEscHandler = null
  }

  // Grip-scoped move/up/cancel bindings — capture routes the events here
  // even outside the root's bounds. Bound refs are kept for teardown.
  _setPointerMoveListeners(grip) {
    this._onPointerMoveBound = (event) => this._onResizePointerMove(event)
    this._onPointerUpBound = (event) => this._onResizePointerUp(event)
    this._onPointerCancelBound = (event) => this._onResizePointerCancel(event)
    grip.addEventListener("pointermove", this._onPointerMoveBound)
    grip.addEventListener("pointerup", this._onPointerUpBound)
    grip.addEventListener("pointercancel", this._onPointerCancelBound)
  }

  _teardownPointerListeners() {
    const grip = this._resize && this._resize.grip
    if (grip && this._onPointerMoveBound) {
      grip.removeEventListener("pointermove", this._onPointerMoveBound)
      grip.removeEventListener("pointerup", this._onPointerUpBound)
      grip.removeEventListener("pointercancel", this._onPointerCancelBound)
    }
    this._onPointerMoveBound = null
    this._onPointerUpBound = null
    this._onPointerCancelBound = null
  }

  // Live preview: compute the clamped target spans from the delta, then
  // coalesce writes to one per animation frame (P3/P14). A foreign pointer's
  // events are ignored — a second pointer must never hijack (or commit) the
  // live gesture.
  _onResizePointerMove(event) {
    const gesture = this._resize
    if (!gesture || event.pointerId !== gesture.pointerId) return
    gesture.last = event
    if (gesture.frame) return
    gesture.frame = window.requestAnimationFrame(() => {
      if (!this._resize) return
      const pending = this._resize.last
      this._resize.frame = null
      if (!pending) return
      this._previewResize(pending.clientX, pending.clientY)
    })
  }

  // The grip sits at inline-end — the LEFT edge under dir="rtl", where
  // growing means dragging toward negative x. The sign flip mirrors the
  // keyboard path's directionOf rule (P4): same gesture, same growth.
  _previewResize(clientX, clientY) {
    const gesture = this._resize
    if (!gesture) return
    const def = this._registry[gesture.gadget_id] || {}
    const metrics = this._gridMetrics()
    const sign = directionOf(this.element) === "rtl" ? -1 : 1
    const dx = ((clientX - gesture.start.x) / metrics.colWidth) * sign
    const dy = (clientY - gesture.start.y) / metrics.rowHeight
    const width = this._clampSpan(gesture.start.width + Math.round(dx), def.min_width)
    const height = this._clampSpan(gesture.start.height + Math.round(dy), def.min_height)
    this._applySpans(gesture.wrapper, { ...gesture.start, width, height })
  }

  _onResizePointerUp(event) {
    const gesture = this._resize
    if (!gesture || gesture.keyboard) return
    if (event.pointerId !== gesture.pointerId) return
    event.preventDefault()
    this._previewResize(event.clientX, event.clientY)
    this._finishResize({})
  }

  _onResizePointerCancel(event) {
    if (this._resize && event && event.pointerId != null && event.pointerId !== this._resize.pointerId) {
      return
    }
    this._finishResize({ cancelled: true })
  }

  // Keyboard resize (P-203): Alt+Shift+Arrows on a FOCUSED resize grip.
  // ArrowUp/Down adjust height, ArrowLeft/Right width (logical: in RTL the
  // horizontal arrows flip — the grid's direction decides, P4). Emits the
  // same payloads as the pointer path with source: "keyboard". Esc during a
  // live pointer resize restores the pre-gesture spans (D3).
  _onKeyDown(event) {
    if (event.key === "Escape" && this._resize) {
      event.preventDefault()
      this._finishResize({ cancelled: true })
      return
    }
    if (!this.editingValue) return
    // A live pointer gesture owns the gadget's spans — a keyboard resize
    // racing it would be last-writer-wins span clobbering. Esc (above) still
    // cancels; every other key waits for the gesture to end.
    if (this._resize) return
    if (!event.altKey || !event.shiftKey) return
    const key = event.key
    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(key)) return

    const grip = event.target.closest ? event.target.closest("[data-work-gadget-resize]") : null
    if (!grip) return
    const wrapper = grip.closest("[data-gadget-id]")
    const gadget_id = wrapper && wrapper.getAttribute("data-gadget-id")
    const entry = gadget_id ? this._entryFor(gadget_id) : null
    if (!entry) return

    event.preventDefault()
    event.stopPropagation()

    const rtl = directionOf(this.element) === "rtl"
    const growX = key === "ArrowRight" ? (rtl ? -1 : 1) : key === "ArrowLeft" ? (rtl ? 1 : -1) : 0
    const growY = key === "ArrowDown" ? 1 : key === "ArrowUp" ? -1 : 0

    const def = this._registry[gadget_id] || {}
    const width = this._clampSpan(entry.width + growX, def.min_width)
    const height = this._clampSpan(entry.height + growY, def.min_height)
    if (width === entry.width && height === entry.height) return // boundary no-op

    this.layoutValue = this.layoutValue.map((item) =>
      item.gadget_id === gadget_id ? { ...item, width, height } : item
    )
    this._applySpans(wrapper, { ...entry, width, height })

    this._emit(GADGET_RESIZE_EVENT, { gadget_id, width, height, source: "keyboard" })
    this._emitLayoutChange("resize", { gadget_id, source: "keyboard" })
  }

  _clampSpan(value, min) {
    const floor = Number.isFinite(min) && min >= 1 ? Math.floor(min) : 1
    return Math.min(MAX_SPAN, Math.max(floor, Math.round(value)))
  }

  // The D1 unit math, measured live (auto-rows-[2.5rem] + gap-4 resolve to
  // px at the viewer's root font size — never hardcode 40).
  _gridMetrics() {
    const grid = this.gridTarget
    const style = window.getComputedStyle(grid)
    const rowHeight = Number.parseFloat(style.gridAutoRows) || ROW_UNIT_FALLBACK
    const gap = Number.parseFloat(style.columnGap) || Number.parseFloat(style.gap) || 0
    const colWidth = (grid.clientWidth - gap * (MAX_SPAN - 1)) / MAX_SPAN
    return { rowHeight, gap, colWidth }
  }

  // Finalize (or cancel) the pointer gesture: commit the previewed spans
  // into the working layout, emit gadget-resize + the resize cause, restore
  // Esc bookkeeping. A cancelled gesture restores the pre-gesture spans.
  _finishResize({ cancelled = false }) {
    const gesture = this._resize
    if (!gesture) return
    this._unbindGestureEsc()
    this._cancelResizeFrame()
    this._teardownPointerListeners()
    this._restoreScrollLocks()
    try {
      if (gesture.grip.hasPointerCapture && gesture.grip.hasPointerCapture(gesture.pointerId)) {
        gesture.grip.releasePointerCapture(gesture.pointerId)
      }
    } catch {
      // already released (cancel path) — nothing to restore
    }

    const finalSpans = cancelled
      ? { ...gesture.start }
      : this._spansOf(gesture.wrapper) || { ...gesture.start }
    this._applySpans(gesture.wrapper, finalSpans)

    if (cancelled) {
      // P-203: Esc/pointercancel restore — layout state untouched, DOM back
      // to the pre-gesture spans.
      this._syncDomToLayout()
      this._resize = null
      return
    }

    this.layoutValue = this.layoutValue.map((item) =>
      item.gadget_id === gesture.gadget_id ? { ...item, ...finalSpans } : item
    )
    this._emit(GADGET_RESIZE_EVENT, {
      gadget_id: gesture.gadget_id,
      width: finalSpans.width,
      height: finalSpans.height,
      source: "pointer",
    })
    this._emitLayoutChange("resize", { gadget_id: gesture.gadget_id, source: "pointer" })
    this._resize = null
  }

  _spansOf(wrapper) {
    const width = Number(wrapper.getAttribute("data-width"))
    const height = Number(wrapper.getAttribute("data-height"))
    if (!Number.isFinite(width) || !Number.isFinite(height)) return null
    return { width, height }
  }

  _cancelResizeFrame() {
    if (this._resize && this._resize.frame) window.cancelAnimationFrame(this._resize.frame)
    if (this._resize) this._resize.frame = null
  }

  // Scroll lock during a pointer resize (touch drag would otherwise scroll
  // the page) — refcounted; teardown always restores (A2.8).
  _pushScrollLock() {
    const body = document.body
    if (!body) return
    const previous = body.style.overflow
    body.style.overflow = "hidden"
    this._scrollLocks.push(previous)
  }

  _restoreScrollLocks() {
    const body = document.body
    // `!this._scrollLocks`: the D1 teardown runs before connect()'s state
    // init on the very first connect — nothing was ever pushed, nothing to
    // restore.
    if (!body || !this._scrollLocks) return
    while (this._scrollLocks.length) {
      body.style.overflow = this._scrollLocks.pop() || ""
    }
  }
}
